import { useCallback, useEffect, useRef } from 'react';
import type { PointerEventHandler } from 'react';
import { useReducedMotion } from 'motion/react';

export interface SpecularProps<T extends HTMLElement = HTMLElement> {
  onPointerMove: PointerEventHandler<T>;
  onPointerLeave: PointerEventHandler<T>;
}

/**
 * Drives the `.g-specular-hover` highlight by writing `--px` / `--py` onto the
 * element as the cursor crosses it.
 *
 * Writes go straight to `element.style` rather than through React state: this
 * fires at pointer frequency, and re-rendering a card sixty times a second to
 * move a gradient would be indefensible. Writes are also coalesced to one per
 * animation frame, because pointermove can outpace paint.
 */
export function useSpecular<T extends HTMLElement = HTMLElement>(enabled = true): SpecularProps<T> {
  const reduceMotion = useReducedMotion();
  const frame = useRef<number | null>(null);
  const active = enabled && !reduceMotion;

  const onPointerMove = useCallback<PointerEventHandler<T>>(
    (event) => {
      // Touch has no hover state, so tracking it would only fire a highlight
      // the moment the finger lands and leave it stranded there.
      if (!active || event.pointerType === 'touch') return;
      if (frame.current !== null) return;
      const element = event.currentTarget;
      const { clientX, clientY } = event;
      frame.current = requestAnimationFrame(() => {
        frame.current = null;
        const rect = element.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;
        element.style.setProperty('--px', `${((clientX - rect.left) / rect.width) * 100}%`);
        element.style.setProperty('--py', `${((clientY - rect.top) / rect.height) * 100}%`);
      });
    },
    [active],
  );

  const onPointerLeave = useCallback<PointerEventHandler<T>>((event) => {
    if (frame.current !== null) {
      cancelAnimationFrame(frame.current);
      frame.current = null;
    }
    const element = event.currentTarget;
    element.style.removeProperty('--px');
    element.style.removeProperty('--py');
  }, []);

  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    },
    [],
  );

  return { onPointerMove, onPointerLeave };
}
